import React, { useState, useRef, useEffect, JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

import apiClient from '../../stateManagers/services/axiosInterceptor';
import NoticeboardSkeletonLoader from '../../skeletons/NoticeboardSkeletonLoader';
import CustomPaginator from '../../components/CustomPaginator';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import GlobalDeleteModal from '../../components/GlobalDeleteModal';

interface LetterheadData {
  _id: string;
  letterhead_id?: string;
  title: string;
  description?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface PaginationData {
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

const LetterheadListing: React.FC = () => {
  const navigate = useNavigate();

  const [letterheads, setLetterheads] = useState<LetterheadData[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [paginationData, setPaginationData] = useState<PaginationData>({
    total: 0, page: 1, limit: 10, total_pages: 1,
  });

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingLetterheadId, setDeletingLetterheadId] = useState<string | null>(null);
  const [deletingLetterhead, setDeletingLetterhead] = useState<LetterheadData | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const toast = useRef<Toast>(null);

  useEffect(() => { fetchLetterheads(); }, [page]);

  const fetchLetterheads = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/api/letterheads?page=${page}&limit=10`);
      if (response.data.status === 'success') {
        await new Promise(resolve => setTimeout(resolve, 600));
        setLetterheads(response.data.letterheads || []);
        setPaginationData(response.data.pagination || {
          total: 0, page: 1, limit: 10, total_pages: 1,
        });
      } else {
        toast.current?.show({
          severity: 'error', summary: 'Error',
          detail: response.data.message || 'Failed to fetch letterheads', life: 4000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error', summary: 'Error',
        detail: err.response?.data?.message || 'Failed to fetch letterheads', life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingLetterheadId) return;
    setDeleteLoading(true);
    try {
      await apiClient.delete(`/api/letterheads/${deletingLetterheadId}`);
      toast.current?.show({
        severity: 'success', summary: 'Success',
        detail: 'Letterhead deleted successfully!', life: 4000,
      });
      setDeleteModalOpen(false);
      setDeletingLetterheadId(null);
      setDeletingLetterhead(null);
      if (letterheads.length === 1 && page > 1) {
        setPage(prev => prev - 1);
      } else {
        fetchLetterheads();
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error', summary: 'Error',
        detail: err.response?.data?.message || 'Failed to delete letterhead', life: 5000,
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const serialNumberTemplate = (_data: LetterheadData, options: ColumnBodyOptions): JSX.Element => {
    const index = options.rowIndex + ((page - 1) * paginationData.limit);
    return (
      <div style={{
        width: '28px', height: '28px', background: '#f9fafb',
        borderRadius: '6px', display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontWeight: '700', fontSize: '12px',
        color: '#6b7280', margin: '0 auto',
      }}>
        {index + 1}
      </div>
    );
  };

  const titleTemplate = (rowData: LetterheadData): JSX.Element => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <div style={{
        width: '38px', height: '38px', borderRadius: '10px',
        background: 'linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '2px solid #f3f4f6', fontSize: '15px', fontWeight: '600',
        color: 'white', flexShrink: 0,
      }}>
        {rowData.title.charAt(0).toUpperCase()}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontWeight: '600', fontSize: '13px', color: '#1f2937',
          marginBottom: '2px', whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {rowData.title}
        </div>
        {rowData.letterhead_id && (
          <div style={{
            fontSize: '10px', color: '#6b7280', fontWeight: '600',
            background: '#f3f4f6', borderRadius: '4px',
            padding: '1px 6px', display: 'inline-block',
            letterSpacing: '0.3px', marginTop: '2px',
          }}>
            {rowData.letterhead_id}
          </div>
        )}
      </div>
    </div>
  );

  const createdByTemplate = (rowData: LetterheadData): JSX.Element => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <div style={{
        width: '38px', height: '38px', borderRadius: '10px',
        background: 'linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '2px solid #f3f4f6', fontSize: '14px', fontWeight: '600',
        color: 'white', flexShrink: 0,
      }}>
        {rowData.created_by?.charAt(0)?.toUpperCase() || '?'}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontWeight: '600', fontSize: '13px', color: '#1f2937',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {rowData.created_by || 'System'}
        </div>
      </div>
    </div>
  );

  const dateTemplate = (rowData: LetterheadData): JSX.Element => {
    const created = new Date(rowData.created_at);
    const updated = new Date(rowData.updated_at);
    return (
      <div>
        <div style={{ fontSize: '13px', color: '#1f2937', fontWeight: '500' }}>
          {created.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
        </div>
        <div style={{ fontSize: '11px', color: '#9ca3af' }}>
          {created.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
        </div>
        {rowData.updated_at !== rowData.created_at && (
          <div style={{ fontSize: '10px', color: '#d1d5db', marginTop: '2px' }}>
            Updated {updated.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
          </div>
        )}
      </div>
    );
  };

  const actionTemplate = (rowData: LetterheadData): JSX.Element => {
    const objectId = rowData._id;
    
    return (
      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
        <Button
          icon="pi pi-pencil"
          className="p-button-text p-button-rounded"
          tooltip="Edit"
          tooltipOptions={{ position: 'top' }}
          style={{ color: '#60a5fa', width: '32px', height: '32px' }}
          onClick={() => {
            if (objectId) navigate(`/letterheads/edit/${objectId}`);
          }}
        />
        <Button
          icon="pi pi-eye"
          className="p-button-text p-button-rounded"
          tooltip="Preview"
          tooltipOptions={{ position: 'top' }}
          style={{ color: '#3b82f6', width: '32px', height: '32px' }}
          onClick={() => {
            if (objectId) navigate(`/letterheads/edit/${objectId}`);
          }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-text p-button-rounded"
          tooltip="Delete"
          tooltipOptions={{ position: 'top' }}
          style={{ color: '#ef4444', width: '32px', height: '32px' }}
          onClick={() => {
            setDeletingLetterheadId(objectId);
            setDeletingLetterhead(rowData);
            setDeleteModalOpen(true);
          }}
        />
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          @keyframes slideIn {
            from { opacity: 0; transform: translateX(-10px); }
            to   { opacity: 1; transform: translateX(0); }
          }
          .p-datatable .p-datatable-tbody > tr {
            animation: slideIn 0.4s ease-out;
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .p-datatable .p-datatable-tbody > tr:hover {
            background: linear-gradient(to right, rgba(65,105,225,0.04) 0%, rgba(65,105,225,0.02) 100%) !important;
            box-shadow: inset 4px 0 0 0 #4169E1;
          }
          .p-button-text:hover { background: #f3f4f6 !important; }
          .p-datatable .p-datatable-thead > tr > th {
            background: #f9fafb !important;
            color: #6b7280 !important;
            font-size: 11px !important;
            font-weight: 700 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.8px !important;
            padding: 16px 12px !important;
            border-bottom: 1px solid #e5e7eb !important;
          }
          .p-datatable .p-datatable-tbody > tr > td {
            padding: 16px 12px !important;
            border-bottom: 1px solid #f3f4f6 !important;
          }
          .p-datatable .p-datatable-tbody > tr:last-child > td {
            border-bottom: none !important;
          }
        `}</style>

        <GlobalHeaderTitle
          title="Letterheads"
          subtitle={`${paginationData.total} total letterheads`}
          icon="pi pi-file"
          buttons={[
            {
              label: 'New Letterhead',
              icon: 'pi pi-plus',
              onClick: () => navigate('/letterheads/new'),
            },
          ]}
        />

        {loading ? (
          <NoticeboardSkeletonLoader />
        ) : (
          <div style={{
            background: 'white', borderRadius: '20px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            border: '1px solid #e5e7eb', overflow: 'hidden', width: '100%',
          }}>
            {letterheads.length > 0 && (
              <div style={{
                padding: '16px 20px', borderBottom: '1px solid #f3f4f6',
                background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                borderRadius: '20px 20px 0 0',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '8px',
                    background: 'linear-gradient(135deg, rgba(65,105,225,0.1) 0%, rgba(91,124,230,0.08) 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <i className="pi pi-list" style={{ fontSize: '14px', color: '#4169E1' }} />
                  </div>
                  <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>
                    Showing {((page - 1) * paginationData.limit) + 1}–{Math.min(page * paginationData.limit, paginationData.total)} of {paginationData.total} letterheads
                  </span>
                </div>
                <div style={{
                  padding: '6px 12px', borderRadius: '8px',
                  background: 'linear-gradient(135deg, rgba(65,105,225,0.1) 0%, rgba(91,124,230,0.08) 100%)',
                  border: '1px solid rgba(65,105,225,0.15)',
                }}>
                  <span style={{ fontSize: '13px', color: '#4169E1', fontWeight: '600' }}>
                    Page {paginationData.page} of {paginationData.total_pages}
                  </span>
                </div>
              </div>
            )}

            <DataTable
              value={letterheads}
              loading={false}
              emptyMessage={
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', padding: '60px 20px', minHeight: '400px',
                }}>
                  <div style={{
                    width: '56px', height: '56px',
                    background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
                    borderRadius: '14px', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', marginBottom: '20px',
                    boxShadow: '0 6px 18px rgba(59,130,246,0.2)',
                  }}>
                    <i className="pi pi-file" style={{ fontSize: '25px', color: 'white' }} />
                  </div>
                  <h3 style={{
                    margin: 0, color: '#1e40af', fontSize: '20px',
                    fontWeight: '700', marginBottom: '8px', textAlign: 'center',
                  }}>
                    No Letterheads Found
                  </h3>
                  <p style={{
                    margin: 0, color: '#64748b', fontSize: '14px', fontWeight: '500',
                    textAlign: 'center', maxWidth: '400px', lineHeight: 1.6,
                  }}>
                    Create your first letterhead to get started
                  </p>
                </div>
              }
            >
              <Column header="#"          body={serialNumberTemplate} style={{ width: '60px' }} />
              <Column header="Title"      body={titleTemplate} />
              <Column header="Created By" body={createdByTemplate}    style={{ width: '220px' }} />
              <Column header="Created At" body={dateTemplate}         style={{ width: '160px' }} />
              <Column header="Actions"    body={actionTemplate}       style={{ width: '120px' }} />
            </DataTable>

            {paginationData.total_pages > 1 && (
              <CustomPaginator
                page={paginationData.page}
                totalPages={paginationData.total_pages}
                totalRecords={paginationData.total}
                pageSize={paginationData.limit}
                onPageChange={(newPage) => setPage(newPage)}
                showInfoBar={false}
              />
            )}
          </div>
        )}
      </div>

      <GlobalDeleteModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeletingLetterheadId(null);
          setDeletingLetterhead(null);
        }}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
        title="Delete Letterhead"
        subtitle="This action cannot be undone. The letterhead will be permanently deleted."
      >
        {deletingLetterhead && (
          <div style={{
            padding: '16px',
            background: 'rgba(239,68,68,0.05)',
            border: '1px solid rgba(239,68,68,0.2)',
            borderRadius: '10px', marginTop: '12px',
          }}>
            <div style={{ fontWeight: 600, color: '#1f2937' }}>{deletingLetterhead.title}</div>
            {deletingLetterhead.letterhead_id && (
              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px', fontWeight: 600 }}>
                ID: {deletingLetterhead.letterhead_id}
              </div>
            )}
            <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
              Created by {deletingLetterhead.created_by}
            </div>
          </div>
        )}
      </GlobalDeleteModal>
    </>
  );
};

export default LetterheadListing;